/**
 * 화면 문구 (react-intl)
 * 예전에는 ko/en/jp/vi/ru 문구를 처음부터 모두 받아서(gzip 약 180KB) 응답 페이지도 안 쓰는 언어까지 받았다
 * → 그 언어가 처음 필요할 때 그 언어 파일만 받는다 (동적 import, 언어별 청크)
 * - 한 번 받은 언어는 펼친(flatten) 결과를 기억해서 다시 받거나 펼치지 않는다 (예전에는 다시 그릴 때마다 펼쳤다)
 * - 언어를 바꾸는 동안은 이전 언어 문구를 그대로 보여준다 (빈 화면 · 키 이름이 보이지 않게)
 * - 처음 화면은 그 언어 파일이 올 때까지 그리지 않는다 (예전에도 메인과 같이 받던 파일)
 */
import { useEffect, useState } from 'react'
import { IntlProvider } from 'react-intl'
import flatten from 'flat'

const loaders = {
    ko: () => import(/* webpackChunkName: "localeKo" */ 'locale/ko.json'),
    en: () => import(/* webpackChunkName: "localeEn" */ 'locale/en.json'),
    jp: () => import(/* webpackChunkName: "localeJp" */ 'locale/jp.json'),
    vi: () => import(/* webpackChunkName: "localeVi" */ 'locale/vi.json'),
    ru: () => import(/* webpackChunkName: "localeRu" */ 'locale/ru.json')
}

const cache = {}        // lang → 펼친 문구
const pending = {}      // lang → 받는 중인 Promise (동시에 여러 곳에서 불러도 한 번만 받는다)

// 받을 때 네트워크가 잠깐 끊기면 몇 번 더 시도한다 (못 받으면 화면이 안 그려져서)
const RETRY = 3
const RETRY_DELAY = 1000

export const normalizeLang = (lang) => loaders[lang] ? lang : 'ko'

function importWithRetry(lang, count = 0) {
    return loaders[lang]().catch(e => {
        if (count >= RETRY) throw e
        return new Promise(resolve => setTimeout(resolve, RETRY_DELAY)).then(() => importWithRetry(lang, count + 1))
    })
}

export function loadLangMessage(clang) {
    const lang = normalizeLang(clang)

    if (cache[lang]) return Promise.resolve(cache[lang])

    if (!pending[lang]) {
        pending[lang] = importWithRetry(lang)
            .then(module => {
                cache[lang] = flatten(module.default || module)
                return cache[lang]
            })
            .catch(e => {
                delete pending[lang]
                throw e
            })
    }

    return pending[lang]
}

// 이미 받은 언어면 바로, 아니면 null (받기는 loadLangMessage / useLangMessage)
export const getLangMessage = (lang) => cache[normalizeLang(lang)] || null

// 그 언어 문구 (받기 전에는 null, 받으면 다시 그린다)
export function useLangMessage(clang) {
    const lang = normalizeLang(clang)

    const [ state, setState ] = useState(() => ({ lang, messages: getLangMessage(lang) }))

    useEffect(() => {
        if (state.lang === lang && state.messages) return

        let alive = true
        loadLangMessage(lang).then(messages => {
            if (alive) setState({ lang, messages })
        }).catch(() => {})

        return () => { alive = false }
    }, [lang, state.lang, state.messages])

    return state
}

export const getLocale = (lang) => {
    const locales = {
        ko: 'ko-KR',
        en: 'en-US',
        jp: 'ja-JP',
        vi: 'vi-VN',
        ru: 'ru-RU'
    }

    return locales[lang] || 'ko-KR'
}

export default (props) => {
    const { lang: clang, children } = props

    // 새 언어를 받는 동안은 이전 언어(state.lang) 문구로 그린다
    const { lang, messages } = useLangMessage(clang || 'ko')

    if (!messages) return null

    return (
        <IntlProvider locale={getLocale(lang)} messages={messages}>
            {children}
        </IntlProvider>
    )
}
